import React, { useState } from "react";
import { Icon, Transition } from "semantic-ui-react";
import { useSelector } from "react-redux";
import { Form } from "@redriver/cinnamon";
import classNames from "classnames";
import {
  ScheduleStepCreateLargeFormat,
  ScheduleLargeFormatGroupArray,
  ScheduleStepEditLargeFormat,
} from "./LargeFormat";
import ScheduleUnitArray from "./ScheduleUnitArray";
import {
  SheetStep,
  cannotCalcMessages,
  lfHasManagedVolInc,
  canSheetBeUpdated,
} from "features/Sheets/ManageSheet";
import {
  getSheetLargeFormatScheduleState,
  getSheetDataState,
  getSheetDetailsSaving,
  getSheetViewType,
  getSheetIpsRatesState,
  getSheetRestrictionsState,
  getSheetRatesState,
  useSheetCalcInfo,
} from "features/Sheets";
import { FixedInput, FixedInputResponsiveLabel } from "components/forms";
import { useCurrentUser } from "features/../../../shared/components/auth";
import {
  Actions,
  Targets,
} from "features/../../../shared/constants/permissions";
import { SheetViewType } from "features/../../../shared/constants/enums";
import { useTranslation } from "react-i18next";
import { ProductScheduleTriggerButton } from "features/Sheets/ManageSheet/Shared";
import AddUnit from "./AddUnit";
import ColumnsHeader from "./ColumnsHeader";
import ScheduleReorderButton from "features/Sheets/ManageSheet/ScheduleStep/ScheduleReorderButton";
import LargeFormatReorderButton from "features/Sheets/ManageSheet/ScheduleStep/LargeFormatReorderButton";
import {
  ImpersonatedUserPermissionCheck,
  useImpersonatedPermission,
} from "modules/ImpersonatedUserPermissions";
import { ListReturnOnInvestments } from "features/Sheets/ManageSheet/ScheduleStep/ReturnOnInvestment";

const scheduleMissingInclusive = (fields) =>
  fields &&
  Array.isArray(fields.systemScheduleItems) &&
  fields.systemScheduleItems.some((x) => !x.includeManagedVol.value) &&
  !fields.systemScheduleItems.some((x) => x.includeManagedVol.value) &&
  !lfHasManagedVolInc(fields.largeFormatGroups);

const ScheduleStep = ({
  sheetId,
  largeFormatEnabled,
  onNextStep,
  onPreviousStep,
  numberOfSchedules,
  utilityMeters,
  calculating,
  displayPrevious,
  displayNext,
  displaySave,
  collapsed,
  toggleVisibility,
  reactRef,
  customerToggle,
  displayCustomerToggle = false,
  setProductScheduleViewable,
  userType,
  clientName,
  crmCompanyId,
}) => {
  const { isMssa } = useSheetCalcInfo();
  const sheetDetailsSaving = useSelector(getSheetDetailsSaving);
  const sheetViewType = useSelector(getSheetViewType);
  const sheetData = useSelector(getSheetDataState);
  const { preventUpdates } = sheetData;
  const lfItems = useSelector(getSheetLargeFormatScheduleState) ?? [];
  const hasLfItems = lfItems.length > 0;
  const {
    unlockAllowedTieredToner,
    unlockOverrideMaximumTieredToner,
    unlockDisableMinVolsLogic,
  } = useSelector(getSheetIpsRatesState);
  const { tieredTonerAvailableToDealer, returnOnInvestmentsAvailable } =
    useSelector(getSheetRestrictionsState);
  const { allowedTieredToner } = useSelector(getSheetRatesState);
  const currentUser = useCurrentUser();
  const { userArea } = currentUser;
  const sheetCanBeUpdated = canSheetBeUpdated(sheetData);
  const [editModalTrigger, setEditModalTrigger] = useState(false);
  const [largeFormatEditIndex, setLargeFormatEditIndex] = useState();
  const [lfEditModalLoaded, setLfEditModalLoaded] = useState(false);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const hideFixedIndicator = !useImpersonatedPermission(
    Targets.SheetAdmin,
    Actions.Edit,
  );
  const { t } = useTranslation("systemSchedule");

  const majorCurrency = process.env.MAJOR_CURRENCY;
  const minorCurrency = process.env.MINOR_CURRENCY;
  const principalMode = !process.env.TRADE_AS_IPS;

  const showUnitsHeader = useImpersonatedPermission(
    Targets.Sheet,
    Actions.ViewStandardScheduleItems,
  );
  const showLFPHeader = useImpersonatedPermission(
    Targets.Sheet,
    Actions.ViewLargeFormatSection,
  );

  const scheduleItemsLimitExceeded = principalMode
    ? numberOfSchedules > 10
    : false;
  const utilityMetersExceeded = principalMode ? utilityMeters > 5 : false;
  const scheduleItemsReachedLimit = principalMode
    ? numberOfSchedules >= 10
    : false;
  const utilityMetersReachedLimit = principalMode ? utilityMeters >= 5 : false;
  return (
    <div ref={reactRef} className="step-container">
      <SheetStep
        onNextStep={onNextStep}
        onPreviousStep={onPreviousStep}
        displayPrevious={displayPrevious}
        displayNext={displayNext}
        displaySave={displaySave}
        calculating={calculating}
        displayCustomerToggle={displayCustomerToggle}
        customerToggle={customerToggle}
        sheetId={sheetId}
        className="schedule-step"
        saveNotice={(data) =>
          scheduleMissingInclusive(data) && (
            <p className="sheet-validation-message sheet-validation-error">
              You must have inclusive volumes
            </p>
          )
        }
        errorMessage={cannotCalcMessages.schedules}
      >
        <div className="schedule-step">
          <div className="schedule-step-header">
            <div className="sheet-step-header" onClick={toggleVisibility}>
              <h5 className="title">{t("title")}</h5>
              {toggleVisibility && (
                <Icon
                  name="dropdown"
                  rotated={collapsed ? "counterclockwise" : null}
                />
              )}
            </div>
          </div>
          <Transition animation="fade down" visible={!collapsed} duration={500}>
            <div>
              <div className="overall-values system-schedule-top-row">
                <div>
                  <Form.Object field="capital" propagateUpdates="always">
                    <FixedInput
                      label={
                        <FixedInputResponsiveLabel
                          label={t("form.deviceQuantityOnSchedule.label")}
                        />
                      }
                      field="totalDevicesOnSchedule"
                      fluid
                      decimalPlaces={0}
                      formatNumber
                      placeholder={t(
                        "form.deviceQuantityOnSchedule.placeholder",
                      )}
                      selectOnFocus
                      className="tiny device-qty"
                      hideIndicator={hideFixedIndicator}
                      minAllowValue={0}
                    />
                  </Form.Object>
                </div>

                <ImpersonatedUserPermissionCheck
                  target={Targets.SheetAdmin}
                  action={Actions.Edit}
                >
                  <div className="admin-fields">
                    <Form.Object field="rates" propagateUpdates="always">
                      {!isMssa &&
                        tieredTonerAvailableToDealer === true &&
                        (sheetViewType === SheetViewType.IpsAdmin ||
                          sheetViewType === SheetViewType.SupplierAdmin) && (
                          <React.Fragment>
                            <Form.RadioGroup
                              inline
                              field="allowedTieredToner"
                              disabled={
                                !unlockAllowedTieredToner || preventUpdates
                              }
                              label={
                                <div className="sheet-radio-title">
                                  <p>{t("form.allowedTierToner.label")}</p>
                                </div>
                              }
                              options={[
                                { value: 1, text: "Yes" },
                                { value: 0, text: "No" },
                              ]}
                              className="required-field sheet-radio-group"
                            />
                            <Form.RadioGroup
                              inline
                              field="overrideMaxTieredToner"
                              disabled={
                                !unlockOverrideMaximumTieredToner ||
                                preventUpdates
                              }
                              label={
                                <div className="sheet-radio-title">
                                  <p>
                                    {t("form.overrideMaximumTieredToner.label")}
                                  </p>
                                </div>
                              }
                              options={[
                                { value: 1, text: "Yes" },
                                { value: 0, text: "No" },
                              ]}
                              className="required-field sheet-radio-group"
                            />
                          </React.Fragment>
                        )}
                      {(sheetViewType === SheetViewType.IpsAdmin ||
                        sheetViewType === SheetViewType.SupplierAdmin) && (
                        <Form.RadioGroup
                          inline
                          field="systemScheduleDisableMinVolsLogic"
                          disabled={
                            !unlockDisableMinVolsLogic || preventUpdates
                          }
                          label={
                            <div className="sheet-radio-title">
                              <p>{t("form.disableMinVolsLogic.label")}</p>
                            </div>
                          }
                          options={[
                            { value: 1, text: "Yes" },
                            { value: 0, text: "No" },
                          ]}
                          className="required-field sheet-radio-group"
                        />
                      )}
                    </Form.Object>
                  </div>
                </ImpersonatedUserPermissionCheck>
              </div>
              <div className="sheet-step-header">
                {sheetCanBeUpdated && (
                  <React.Fragment>
                    <ImpersonatedUserPermissionCheck
                      target={Targets.Sheet}
                      action={Actions.ViewStandardScheduleItems}
                    >
                      <AddUnit
                        sheetId={sheetId}
                        hideFixedIndicator={hideFixedIndicator}
                        userType={userType}
                        disabled={
                          preventUpdates ||
                          scheduleItemsLimitExceeded ||
                          scheduleItemsReachedLimit
                        }
                        userArea={userArea}
                        currentUser={currentUser}
                        showTooltip={
                          scheduleItemsLimitExceeded ||
                          scheduleItemsReachedLimit
                        }
                      />
                    </ImpersonatedUserPermissionCheck>

                    {largeFormatEnabled && (
                      <ImpersonatedUserPermissionCheck
                        target={Targets.Sheet}
                        action={Actions.ViewLargeFormatSection}
                      >
                        <ScheduleStepCreateLargeFormat
                          sheetId={sheetId}
                          sheetViewType={sheetViewType}
                          showTooltip={
                            scheduleItemsLimitExceeded ||
                            utilityMetersExceeded ||
                            scheduleItemsReachedLimit ||
                            utilityMetersReachedLimit
                          }
                          disabled={
                            preventUpdates ||
                            sheetDetailsSaving ||
                            scheduleItemsLimitExceeded ||
                            utilityMetersExceeded ||
                            scheduleItemsReachedLimit ||
                            utilityMetersReachedLimit
                          }
                        />
                      </ImpersonatedUserPermissionCheck>
                    )}
                  </React.Fragment>
                )}
              </div>
              <div className="large-format-create"></div>
              <div
                className={classNames(
                  "schedule-container",
                  popoverVisible && "disable-overflow",
                )}
              >
                <div
                  onKeyDown={(e) => {
                    //prevent enter key propagation
                    if (e.keyCode === 13) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  {(hasLfItems || numberOfSchedules > 0) && (
                    <ColumnsHeader
                      tieredTonerAvailableToDealer={
                        tieredTonerAvailableToDealer
                      }
                      allowedTieredToner={allowedTieredToner}
                      majorCurrency={majorCurrency}
                      minorCurrency={minorCurrency}
                      numberOfSchedules={numberOfSchedules}
                      showHeader={showUnitsHeader}
                      forLFP={false}
                      isMssa={isMssa}
                    />
                  )}
                </div>
                <ImpersonatedUserPermissionCheck
                  target={Targets.Sheet}
                  action={Actions.ViewStandardScheduleItems}
                >
                  <ScheduleUnitArray
                    calculating={calculating}
                    sheetId={sheetId}
                    setPopoverVisible={setPopoverVisible}
                  ></ScheduleUnitArray>
                </ImpersonatedUserPermissionCheck>
                <div
                  onKeyDown={(e) => {
                    //prevent enter key propagation
                    if (e.keyCode === 13) {
                      e.preventDefault();
                      e.stopPropagation();
                    }
                  }}
                >
                  {hasLfItems && numberOfSchedules != 0 && (
                    <ImpersonatedUserPermissionCheck
                      target={Targets.Sheet}
                      action={Actions.ViewLargeFormatSection}
                    >
                      <ColumnsHeader
                        tieredTonerAvailableToDealer={
                          tieredTonerAvailableToDealer
                        }
                        allowedTieredToner={allowedTieredToner}
                        majorCurrency={majorCurrency}
                        minorCurrency={minorCurrency}
                        numberOfSchedules={numberOfSchedules}
                        showHeader={!(showUnitsHeader && showLFPHeader)}
                        forLFP
                        isMssa={isMssa}
                      />
                    </ImpersonatedUserPermissionCheck>
                  )}
                </div>
                <ImpersonatedUserPermissionCheck
                  target={Targets.Sheet}
                  action={Actions.ViewLargeFormatSection}
                >
                  <ScheduleLargeFormatGroupArray
                    hasAnyItems={hasLfItems}
                    sheetId={sheetId}
                    sheetDetailsSaving={sheetDetailsSaving}
                    setEditModalTrigger={() =>
                      setEditModalTrigger(!editModalTrigger)
                    }
                    setLargeFormatEditIndex={setLargeFormatEditIndex}
                  ></ScheduleLargeFormatGroupArray>
                </ImpersonatedUserPermissionCheck>
                <ScheduleStepEditLargeFormat
                  sheetId={sheetId}
                  editModalTrigger={editModalTrigger}
                  largeFormatEditIndex={largeFormatEditIndex}
                  setEditModalTrigger={setEditModalTrigger}
                  setLfEditModalLoaded={setLfEditModalLoaded}
                  lfEditModalLoaded={lfEditModalLoaded}
                  sheetViewType={sheetViewType}
                  clientName={clientName}
                  crmCompanyId={crmCompanyId}
                />
              </div>
              <div>
                {scheduleItemsLimitExceeded && (
                  <span className="calc-error">
                    {t("Maximum_Limit_Reached")}
                  </span>
                )}
                {scheduleItemsLimitExceeded && utilityMetersExceeded && (
                  <span className="calc-error"> / </span>
                )}
                {utilityMetersExceeded && (
                  <span className="calc-error">
                    {t("Maximum_Utility_Limit_Reached")}
                  </span>
                )}
              </div>
              <div style={{ display: "flex", alignItems: "center" }}>
                <ScheduleReorderButton />
                <LargeFormatReorderButton />
              </div>
              {isMssa && returnOnInvestmentsAvailable && (
                <ListReturnOnInvestments
                  dealerOfficeId={sheetData?.dealerOfficeId}
                  disabled={
                    !sheetCanBeUpdated || preventUpdates || sheetDetailsSaving
                  }
                />
              )}
              <ProductScheduleTriggerButton
                setProductScheduleViewable={setProductScheduleViewable}
              />
            </div>
          </Transition>
        </div>
      </SheetStep>
    </div>
  );
};

export default ScheduleStep;
